import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button, Grid } from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBackIos';
import ArrowForward from '@mui/icons-material/ArrowForwardIos';
import { Card, Typography, Stepper, Step, StepButton, StepLabel, useTheme, useMediaQuery } from '@mui/material';

const FacilityStepper = ({ activeStep, steps, onStepClick, isImage }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const overMd = useMediaQuery(theme.breakpoints.up('md'));

  const [stepperOrientation, setStepperOrientation] = useState('horizontal');

  const forceUpdate = () => {
    if (window.innerWidth < 600) setStepperOrientation('vertical');
    else setStepperOrientation('horizontal');
  };

  const resize = () => forceUpdate();

  useEffect(() => {
    if (window.innerWidth < 600) {
      setStepperOrientation('vertical');
    }
    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleActiveStepIndex = steps.filter(s => s.visible()).findIndex(step => step.id === activeStep);

  const getPreviousVisibleStep = stepIndex => {
    stepIndex--;
    if (stepIndex < 0) {
      return null;
    }
    const step = steps[stepIndex];
    return step.visible() ? step : getPreviousVisibleStep(stepIndex);
  };

  const getNextVisibleStep = stepIndex => {
    if (stepIndex === -1) {
      return null;
    }

    stepIndex++;
    if (stepIndex >= steps.length) {
      return null;
    }

    const step = steps[stepIndex];
    return step.visible() ? step : getNextVisibleStep(stepIndex);
  };

  const previousDisabled = (() => {
    const step = getPreviousVisibleStep(activeStep);
    return !step || step.disabled;
  })();

  const nextDisabled = (() => {
    const step = getNextVisibleStep(activeStep);
    return !step || step.disabled;
  })();

  const onPreviousClick = () => {
    const step = getPreviousVisibleStep(activeStep);
    onStepClick(step);
  };

  const onNextClick = () => {
    const step = getNextVisibleStep(activeStep);
    onStepClick(step);
  };

  return (
    <Card disablePadding sx={{ p: 2 }}>
      <Grid container alignItems='center' spacing={3}>
        {!isImage && overMd && (
          <Grid item>
            <Button color='primary' autoFocus disabled={previousDisabled} onClick={onPreviousClick}>
              <ArrowBack />
              {t('Previous')}
            </Button>
          </Grid>
        )}
        <Grid item xs>
          <Grid container>
            <Grid item xs={12}>
              <Stepper activeStep={visibleActiveStepIndex} orientation={stepperOrientation}>
                {steps.map((step, idx) => {
                  return (
                    step.visible() && (
                      <Step disabled={step.disabled} key={step.id}>
                        <StepButton
                          onClick={() => onStepClick(step)}
                          disabled={step.disabled}
                          optional={
                            <Typography variant='caption' color='error'>
                              {step.error}
                            </Typography>
                          }
                        >
                          {idx === visibleActiveStepIndex ? (
                            <StepLabel error={step.error}>
                              <b>{t(step.title)}</b>
                            </StepLabel>
                          ) : (
                            <StepLabel error={step.error}>{t(step.title)}</StepLabel>
                          )}
                        </StepButton>
                      </Step>
                    )
                  );
                })}
              </Stepper>
            </Grid>
          </Grid>
        </Grid>
        {!isImage && overMd && (
          <Grid item>
            <Button color='primary' autoFocus disabled={nextDisabled} onClick={onNextClick}>
              {t('Next')}
              <ArrowForward />
            </Button>
          </Grid>
        )}
      </Grid>
    </Card>
  );
};

FacilityStepper.propTypes = {
  steps: PropTypes.array.isRequired,
  onStepClick: PropTypes.func.isRequired,
  activeStep: PropTypes.number.isRequired,
  isImage: PropTypes.bool
};

export default FacilityStepper;
